import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { correctionService } from "../../api/correctionService";
import Badge from "../../components/ui/Badge";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { formatManilaTime } from "../../utils/manilaTime";
import { parseDateKey } from "../../utils/date";
import { cx } from "../../utils/classNames";
import { CORRECTION_KEY, CORRECTION_STATUS } from "./correctionUtils";
import ui from "../../components/ui/ui.module.css";
import { approverGroupText } from "../../../utils/waitingFor";

const times = (t) =>
  t
    ? `${t.checkInTime ? formatManilaTime(t.checkInTime) : "—"} – ${t.checkOutTime ? formatManilaTime(t.checkOutTime) : "—"}`
    : "no record";

// "My correction requests": newest first, cancel while pending. Hidden when empty.
const MyCorrections = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [CORRECTION_KEY, "mine"],
    queryFn: () => correctionService.getMine(),
  });
  const cancel = useMutation({
    mutationFn: (id) => correctionService.cancel(id),
    onSuccess: () => {
      toast.success("Correction cancelled.");
      queryClient.invalidateQueries({ queryKey: [CORRECTION_KEY] });
      queryClient.invalidateQueries({ queryKey: ["me:dtr"] });
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Unable to cancel the correction.")),
  });
  const rows = Array.isArray(query.data)
    ? query.data
    : (query.data?.data ?? []);
  if (query.isLoading || !rows.length) return null;

  return (
    <div className={ui.card}>
      <div className={ui.cardHeader}>
        <span className={ui.cardTitle}>My correction requests</span>
      </div>
      <div className={ui.tableWrapper}>
        <table className={ui.table}>
          <thead>
            <tr>
              <th>Day</th>
              <th>Was</th>
              <th>Asked for</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const st = CORRECTION_STATUS[c.status] ?? {
                label: c.status,
                tone: "gray",
              };
              return (
                <tr key={c.id}>
                  <td className={ui.noWrap}>
                    {parseDateKey(c.date)?.toLocaleDateString("en-PH", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td className={ui.noWrap}>{times(c.original)}</td>
                  <td className={ui.noWrap}>{times(c.requested)}</td>
                  <td>
                    <Badge tone={st.tone}>{st.label}</Badge>
                    {c.status === "PENDING" &&
                      (c.waitingFor?.length > 0 || c.managersAbove) && (
                        <div className={ui.hint}>
                          Approvers (any one decides):{" "}
                          {approverGroupText(c.waitingFor, c.managersAbove)}
                        </div>
                      )}
                    {c.reviewRemarks && (
                      <div className={ui.hint}>“{c.reviewRemarks}”</div>
                    )}
                  </td>
                  <td className="text-end">
                    {c.status === "PENDING" && c.canCancel !== false && (
                      <button
                        type="button"
                        className={cx(ui.button, ui.secondary, ui.buttonSmall)}
                        onClick={() => cancel.mutate(c.id)}
                        disabled={cancel.isPending}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyCorrections;
